/* eslint-disable react/destructuring-assignment */
import React, {
  useContext, useEffect, useMemo, useRef, useState,
} from 'react';

import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  FormControl, InputLabel, MenuItem, Select,
} from '@mui/material';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { FORM_DEBOUNCE_TIME } from '../../../../../config/deviceConfig/Values';
import { ITEM_DEFAULTS } from '../../../../../config/deviceConfig/item_defaults';
import { IDFField } from '../../../../../config/deviceConfig/types';
import { ApolloAuthContext } from '../../../../../store/Apollo/ApolloContext';
import { FormResetAction, IFormResetRef } from '../../../ProDeviceCreateEdit/types';
import FieldService, { IPin } from '../../../ProDeviceCreateEdit/services/pro-device.service';
import { appConsoleLog, appConsoleError } from '../../../../../utility/appUtils';

const FIELD_NAME = ITEM_DEFAULTS.ITEM_CREATOR.DEVICE_FIELDS.pin_common;
type ComponentProps = {
  fieldConfig: IDFField;
  validateForm: number;
  deviceInfo: any;
  resetForm: IFormResetRef;
  onChange: (payload: any, isInitializing?: boolean) => void;
};
interface IformData {
  pin: string;
}
// eslint-disable-next-line import/prefer-default-export
export const ProDeviceFieldPinCommon: React.FunctionComponent<ComponentProps> = (props) => {
  const [deb, setDeb] = useState<BehaviorSubject<{values: IformData; errors: any} | null>>();

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const onSubmitForm = (formData: IformData) => {};

  const userProfile = useContext(ApolloAuthContext);
  const [pins, setPins] = useState<IPin[]>([]);
  // get details pins
  useEffect(() => {
    if (props.fieldConfig == null) {
      return;
    }
    const init = async () => {
      try {
        const res = await FieldService.proDCGetPins(
          userProfile.apollo_client,
          props.deviceInfo.prodc_id,
          props.fieldConfig.extra,
        );
        appConsoleLog('pins', pins);
        setPins(res.pins);
      } catch (err) {
        appConsoleError(err);
      }
    };
    init();
  }, [props.fieldConfig]);

  const sendDefaults = () => {
    if (!props.fieldConfig || !deb) {
      return;
    }
    deb.next({
      values: {
        pin: props.fieldConfig.default,
      },
      errors: {},
    });
  };

  /**
   * Resetting or changing form data from parent
   */
  const refFormReset = useRef<IFormResetRef>({
    ref: 0,
    action: '',
    info: {},
  });
  useEffect(() => {
    if (
      formRef.current
      && props.resetForm.ref > 0
      && props.resetForm.ref > refFormReset.current.ref
    ) {
      refFormReset.current = props.resetForm;

      if (props.resetForm.action === FormResetAction.toCurrentInfo) {
        const values = props.resetForm.info.settings[props.fieldConfig.field];

        formRef.current.setValues({
          pin: values,
        });
      } else {
        formRef.current.resetForm();
      }
    }
  }, [props.resetForm]);
  const initilized = useRef(0);
  useEffect(() => {
    const b = new BehaviorSubject<{values: IformData; errors: any} | null>(null);
    setDeb(b);
    const sub = b
      .pipe(
        debounceTime(FORM_DEBOUNCE_TIME),
        map((v) => JSON.stringify(v)),
        distinctUntilChanged(),
        map((v) => JSON.parse(v)),
      )
      .subscribe((v: {values: IformData; errors: any} | null) => {
        if (v == null || props.fieldConfig == null) {
          return;
        }

        const { values, errors } = v;
        const infoData: any = {
          settings: {},
        };
        infoData.settings[props.fieldConfig.field] = values.pin;

        if (initilized.current === 0) {
          props.onChange(
            {
              action: 'data',
              form: FIELD_NAME,
              data: infoData,
              errors,
            },
            true,
          );
          initilized.current = 1;
        } else {
          props.onChange({
            action: 'data',
            form: FIELD_NAME,
            data: infoData,
            errors,
          });
        }
      });

    // send once default data
    sendDefaults();
    return () => {
      sub.unsubscribe();
    };
  }, [props.fieldConfig]);
  const formRef = useRef<any>();
  useEffect(() => {
    if (formRef && formRef.current !== undefined && props.validateForm !== 0) {
      formRef.current.submitForm();
    }
  }, [props.validateForm]);

  let isEditable = props.fieldConfig != null && props.fieldConfig.editable === true;
  if (isEditable) {
    if (props.deviceInfo.settings[`has_${props.fieldConfig.field}`] === false) {
      isEditable = false;
    }
  }

  return isEditable === true ? (
    <div>
      <Formik
        initialValues={{
          pin: props.fieldConfig?.default,
        }}
        innerRef={formRef}
        validationSchema={Yup.object().shape({
          pin: Yup.string().required('Required'),
        })}
        validateOnChange
        onSubmit={onSubmitForm}
      >
        {({
          errors, handleBlur, handleChange, touched, values,
        }) => {
          deb?.next({
            values,
            errors,
          });
          return (
            <form>
              <div className="text-fields-flex">
                <div>
                  <FormControl
                    size="small"
                    variant="outlined"
                    className="dynamic-ui-form-control"
                  >
                    <InputLabel id="pin-label1" error={Boolean(touched.pin && errors.pin)}>
                      Pin:
                    </InputLabel>
                    <Select
                      labelId="pin-label1"
                      value={pins.length > 0 ? values.pin : ''}
                      name="pin"
                      onBlur={handleBlur}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      label="Pin"
                    >
                      {pins.map((pin) => (
                        <MenuItem key={pin.pin} value={pin.pin}>
                          {pin.display_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  ) : (
    <div />
  );
};
